import { useState, useEffect, useCallback } from 'react';
import { get, post, patch } from '@vigilo/api-client';
import type { User } from '@vigilo/shared';
import { useMockMode } from '@/shell/MockModeContext';
import { useApp } from '@/shell/AppContext';

// ─── Mock data ────────────────────────────────────────────────────────────────

const ORG_ID = 'org-vigilo';
const CROWD_USER_ROLES = new Set<User['role']>([
  'crowd_analyst',
  'command_supervisor',
  'system_admin',
]);

const MOCK_USERS: User[] = [
  {
    id:          'user-001',
    email:       'analyst@vigilo.dev',
    displayName: 'Sam Chen',
    role:        'crowd_analyst',
    team:        'Alpha Team',
    orgId:       ORG_ID,
    active:      true,
    lastSeen:    new Date().toISOString(),
  },
  {
    id:          'user-002',
    email:       'analyst2@vigilo.dev',
    displayName: 'Priya Sharma',
    role:        'crowd_analyst',
    team:        'Bravo Team',
    orgId:       ORG_ID,
    active:      true,
    lastSeen:    new Date(Date.now() - 2 * 60 * 1000).toISOString(),
  },
  {
    id:          'user-003',
    email:       'analyst3@vigilo.dev',
    displayName: 'Jana Kovač',
    role:        'crowd_analyst',
    team:        'Charlie Team',
    orgId:       ORG_ID,
    active:      false,
    lastSeen:    new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id:          'user-004',
    email:       'supervisor@vigilo.dev',
    displayName: 'Marco Rossi',
    role:        'command_supervisor',
    team:        'Alpha Team',
    orgId:       ORG_ID,
    active:      true,
    lastSeen:    new Date().toISOString(),
  },
  {
    id:          'user-005',
    email:       'operator@vigilo.dev',
    displayName: 'Admin User',
    role:        'system_admin',
    team:        null,
    orgId:       ORG_ID,
    active:      true,
    lastSeen:    new Date(Date.now() - 14 * 60 * 1000).toISOString(),
  },
];

// ─── Types ────────────────────────────────────────────────────────────────────

export interface CreateUserPayload {
  email:       string;
  displayName: string;
  role:        User['role'];
  team?:       string;
  password:    string;
}

export interface UpdateUserPayload {
  displayName?: string;
  role?:        User['role'];
  team?:        string | null;
}

export interface UsersHookState {
  users:        User[];
  loading:      boolean;
  error:        string | null;
  createUser:   (payload: CreateUserPayload) => Promise<void>;
  updateUser:   (id: string, payload: UpdateUserPayload) => Promise<void>;
  deactivate:   (id: string) => Promise<void>;
  reactivate:   (id: string) => Promise<void>;
  refresh:      () => Promise<void>;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

export function generateTempPassword(): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
  let pw = '';
  for (let i = 0; i < 12; i++) {
    pw += chars[Math.floor(Math.random() * chars.length)];
  }
  return pw + '!1';
}

function unwrap<T>(body: unknown): T {
  if (Array.isArray(body)) return body as T;
  const b = body as { data?: T };
  return (b.data ?? body) as T;
}

function isCrowdUser(user: User): boolean {
  return CROWD_USER_ROLES.has(user.role);
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useUsers(): UsersHookState {
  const { isMock } = useMockMode();
  const { currentUser } = useApp();
  const [users,   setUsers]   = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState<string | null>(null);

  const fetchUsers = useCallback(async () => {
    if (isMock) {
      setUsers(MOCK_USERS);
      setLoading(false);
      return;
    }
    try {
      setError(null);
      const body = await get<User[]>('/users');
      setUsers(unwrap<User[]>(body).filter(isCrowdUser));
    } catch {
      setError('Failed to load users.');
    } finally {
      setLoading(false);
    }
  }, [isMock]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const createUser = useCallback(async (payload: CreateUserPayload): Promise<void> => {
    if (isMock) {
      const newUser: User = {
        id:          `user-${Date.now()}`,
        email:       payload.email,
        displayName: payload.displayName,
        role:        payload.role,
        team:        payload.team ?? null,
        orgId:       ORG_ID,
        active:      true,
        lastSeen:    null,
      };
      setUsers(prev => [...prev, newUser]);
      return;
    }
    const body = await post<User>('/users', {
      ...payload,
      orgId: currentUser.orgId,
    });
    const user = unwrap<User>(body);
    if (isCrowdUser(user)) setUsers(prev => [...prev, user]);
  }, [currentUser.orgId, isMock]);

  const updateUser = useCallback(async (id: string, payload: UpdateUserPayload): Promise<void> => {
    if (isMock) {
      setUsers(prev => prev.map(u => u.id === id ? { ...u, ...payload } : u));
      return;
    }
    const body = await patch<User>(`/users/${id}`, payload);
    const user = unwrap<User>(body);
    setUsers(prev => isCrowdUser(user)
      ? prev.map(u => u.id === id ? user : u)
      : prev.filter(u => u.id !== id));
  }, [isMock]);

  const deactivate = useCallback(async (id: string): Promise<void> => {
    if (isMock) {
      setUsers(prev => prev.map(u => u.id === id ? { ...u, active: false } : u));
      return;
    }
    const body = await post<User>(`/users/${id}/deactivate`, {});
    const user = unwrap<User>(body);
    if (isCrowdUser(user)) setUsers(prev => prev.map(u => u.id === id ? user : u));
  }, [isMock]);

  const reactivate = useCallback(async (id: string): Promise<void> => {
    if (isMock) {
      setUsers(prev => prev.map(u => u.id === id ? { ...u, active: true } : u));
      return;
    }
    const body = await post<User>(`/users/${id}/activate`, {});
    const user = unwrap<User>(body);
    if (isCrowdUser(user)) setUsers(prev => prev.map(u => u.id === id ? user : u));
  }, [isMock]);

  return { users, loading, error, createUser, updateUser, deactivate, reactivate, refresh: fetchUsers };
}
